import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Inject,
  Input,
  NgModule,
  PLATFORM_ID,
  Renderer2,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { CommonModule, isPlatformServer } from '@angular/common';
import { SvgMedia } from '@nx-icilalune/drupal-types';
import { ComponentStore } from '@ngrx/component-store';
import { WINDOW } from '@ng-web-apis/common';
import { fromEvent, Observable, tap } from 'rxjs';

@Component({
  selector: 'i2l-media-svg',
  template: ` <img #imgTarget /> `,
  styleUrls: ['./media-svg.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MediaSvgComponent
  extends ComponentStore<{
    isInViewport?: boolean;
    loading: boolean;
    loaded: boolean;
    media?: SvgMedia;
  }>
  implements AfterViewInit
{
  media$ = this.select((s) => s.media);

  @Input()
  set svgMedia(svgMedia: SvgMedia | undefined) {
    this.patchState({ media: svgMedia });
  }

  @ViewChild('imgTarget') imgTarget!: ElementRef<HTMLImageElement>;

  setMedia = this.effect((media$: Observable<SvgMedia | undefined>) => {
    return media$.pipe(
      tap((media) => {
        if (media?.url && this.imgTarget) {
          const { nativeElement } = this.imgTarget;
          this.checkLoading(fromEvent(nativeElement, 'load'));
          this.patchState({ loading: true });
          nativeElement.src = media.url;
          if (media.alt) {
            nativeElement.alt = media.alt;
          }
          if (this.isInViewport()) {
            nativeElement.loading = 'eager';
          } else {
            nativeElement.loading = 'lazy';
          }
        }
      })
    );
  });

  checkLoading = this.effect((loadEvent$: Observable<any>) => {
    return loadEvent$.pipe(
      tap((event) => {
        this.patchState({ loading: false, loaded: true });
        this.el.nativeElement.classList.add('loaded');
      })
    );
  });

  constructor(
    private renderer: Renderer2,
    private el: ElementRef<HTMLElement>,
    @Inject(PLATFORM_ID) platformId: string,
    @Inject(WINDOW) private window: Window
  ) {
    super({ loading: true, loaded: false });
  }

  ngAfterViewInit(): void {
    this.setMedia(this.media$);
  }

  onImageLoaded() {
    const { nativeElement } = this.el;
    nativeElement.classList.add('loaded');
  }

  isInViewport() {
    const { nativeElement } = this.el;
    const rect = nativeElement.getBoundingClientRect();
    return (
      rect.top >= 0 &&
      rect.left >= 0 &&
      rect.bottom <= this.window.innerHeight &&
      rect.right <= this.window.innerWidth
    );
  }
}

@NgModule({
  imports: [CommonModule],
  declarations: [MediaSvgComponent],
  exports: [MediaSvgComponent],
})
export class MediaSvgComponentModule {}
